import { RouteErrorBoundary } from '@/components/ErrorBoundary';
import AccessManagement from '@/components/AccessManagement';
import { CUSTOM_ROLES_ENABLED } from '@/utils/featureFlags';
import Loading from '@/components/Loading/Loading';
import { ROLE } from '@/utils/enum';
import { lazy, Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';

import Layout from '../themes';
import AdminRoute from './AdminRoute';
import InsightsRoute from './InsightsRoute';
import RequirePermission from './RequirePermission';
import ExcludeRoute from './ExcludeRoute';
import PrivateRoute from './PrivateRoute';
import SuperOwnerRoute from './SuperOwnerRoute';
import ClientLayout from './ClientLayout';
import RootRedirect from './RootRedirect';

// Auth
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const LoginSuccess = lazy(() => import('@/pages/login-success/page'));
const TwoFa = lazy(() => import('@/pages/TwoFa/TwoFa'));
const Unauthorized = lazy(() => import('@/pages/Unauthorized'));
const Invite = lazy(() => import('@/pages/Invite'));
const Login = lazy(() => import('@/pages/Login'));
const Password = lazy(() => import('@/pages/Password'));
const McpAuthorize = lazy(() => import('@/pages/McpAuthorize'));

// Public
const About = lazy(() => import('@/pages/About'));
const Demo = lazy(() => import('@/pages/Demo'));
const FacebookDeletion = lazy(() => import('@/pages/FacebookDeletion'));
const Feedback = lazy(() => import('@/pages/Feedback'));
const VoiceDemo = lazy(() => import('@/pages/VoiceDemo'));
const VoiceAgentPublic = lazy(() => import('@/pages/VoiceAgentPublic'));

// Global
const Accounting = lazy(() => import('@/pages/Accounting'));
const Clients = lazy(() => import('@/pages/Clients'));
const Insights = lazy(() => import('@/pages/Insights'));
const NpsReport = lazy(() => import('@/pages/NpsReport'));
const UserManual = lazy(() => import('@/pages/UserManual'));
const Profile = lazy(() => import('@/pages/Profile'));
const Settings = lazy(() => import('@/pages/Settings'));
const AuditTab = lazy(() => import('@/pages/AuditTab'));
const Documentation = lazy(() => import('@/pages/Documentation'));
const HelpResources = lazy(() => import('@/pages/HelpResources'));
const QueryFeedbacks = lazy(() => import('@/pages/QueryFeedback'));

// Global — standalone Beacon
const BeaconInsights = lazy(() => import('@/pages/BeaconInsights'));
const BeaconImplement = lazy(() => import('@/pages/BeaconImplement'));
const BeaconImplementSelfServe = lazy(
  () => import('@/pages/BeaconImplement/SelfServe')
);
const BeaconImplementAgency = lazy(
  () => import('@/pages/BeaconImplement/AgencyHandoff')
);

// Client — core
const Conversations = lazy(() => import('@/pages/Conversations'));
const Leads = lazy(() => import('@/pages/Leads'));
const Theme = lazy(() => import('@/pages/Theme'));
const SubscribedPages = lazy(() => import('@/pages/SubscribedPages'));
const HubspotTickets = lazy(() => import('@/pages/Hubspot'));
const Report = lazy(() => import('@/pages/Report'));

// Client — features
const Beacon = lazy(() => import('@/pages/Beacon'));
const BeaconLive = lazy(() => import('@/pages/BeaconLive'));
const AeoQuery = lazy(() => import('@/pages/AeoQuery'));
const AnalyticsDashboard = lazy(() => import('@/pages/GoogleAnalytics'));
const Training = lazy(() => import('@/pages/TrainingTab'));

// Client — admin / control centre
const Account = lazy(() => import('@/pages/Account'));
const Team = lazy(() => import('@/pages/Team'));
const Branches = lazy(() => import('@/pages/Branches'));
const BulkBranches = lazy(() => import('@/pages/BulkBranches'));
const RedisJobs = lazy(() => import('@/pages/RedisJobs'));
const Platform = lazy(
  () => import('@/components/Integrations/Facebook/Platform')
);
const Integrations = lazy(() => import('@/pages/Integrations'));
const Billing = lazy(() => import('@/pages/Billing'));

// Client — automation & campaigns
const Rule = lazy(() => import('@/pages/Rule'));
const PaymentMembers = lazy(() => import('@/pages/PaymentMembers'));
const PaymentMemberStatus = lazy(() => import('@/pages/PaymentMemberStatus'));
const BulkRule = lazy(() => import('@/pages/BulkRule'));
const ReEngage = lazy(() => import('@/pages/Reengage'));

// Agent Pages
const Clarion = lazy(() => import('@/pages/Clarion'));
const ClarionCallRecords = lazy(() => import('@/pages/Clarion/CallRecords'));
const Ember = lazy(() => import('@/pages/Ember'));
const Nova = lazy(() => import('@/pages/Nova'));
const Atlas = lazy(() => import('@/pages/Atlas'));
const AtlasMemberRecords = lazy(() => import('@/pages/Atlas/MemberRecords'));

// Pulse pages
const ChatSection = lazy(() => import('@/pages/PulseChat'));
const BenchmarkDashboard = lazy(() => import('@/pages/Nova/Benchmark'));
const Improvement = lazy(() => import('@/pages/Nova/Improvement'));
const Learnings = lazy(() => import('@/pages/Nova/Learnings'));
const Performance = lazy(() => import('@/pages/Performance'));
const Templates = lazy(() => import('@/pages/Template'));
const AutomationDashboard = lazy(
  () => import('@/components/Performance/AutomationDashboard')
);
const TemplateDashboard = lazy(
  () => import('@/components/Performance/TemplateDashboard')
);
const ScheduleReport = lazy(() => import('@/pages/ScheduledReport'));

function Router() {
  const globalRoutes = [
    {
      index: true,
      element: <RootRedirect />,
    },
    {
      path: 'clients',
      element: (
        <SuperOwnerRoute>
          <Clients />
        </SuperOwnerRoute>
      ),
    },
    {
      path: 'profile',
      element: <Profile />,
    },
    {
      path: 'settings',
      element: (
        <SuperOwnerRoute>
          <Settings />
        </SuperOwnerRoute>
      ),
    },
    {
      path: 'invite',
      element: (
        <SuperOwnerRoute>
          <Invite />
        </SuperOwnerRoute>
      ),
    },
    {
      path: 'audit',
      element: (
        <AdminRoute permission='conversations:read'>
          <AuditTab />
        </AdminRoute>
      ),
    },
    {
      path: 'metrics',
      element: (
        <SuperOwnerRoute>
          <Insights />
        </SuperOwnerRoute>
      ),
    },
    {
      path: 'manuals',
      element: (
        <SuperOwnerRoute>
          <UserManual />
        </SuperOwnerRoute>
      ),
    },
    {
      path: 'accounting',
      element: (
        <SuperOwnerRoute>
          <Accounting />
        </SuperOwnerRoute>
      ),
    },
    {
      path: 'nps-report',
      element: (
        <SuperOwnerRoute>
          <NpsReport />
        </SuperOwnerRoute>
      ),
    },
    {
      path: 'resources',
      element: <HelpResources />,
    },
    {
      path: 'beacon/insights',
      element: (
        <SuperOwnerRoute>
          <BeaconInsights />
        </SuperOwnerRoute>
      ),
    },
    {
      path: 'beacon/implement/:slug',
      element: (
        <AdminRoute permission='beacon:read'>
          <BeaconImplement />
        </AdminRoute>
      ),
    },
    {
      path: 'beacon/implement/:slug/self-serve',
      element: (
        <AdminRoute permission='beacon:read'>
          <BeaconImplementSelfServe />
        </AdminRoute>
      ),
    },
    {
      path: 'beacon/implement/:slug/agency',
      element: (
        <AdminRoute permission='beacon:read'>
          <BeaconImplementAgency />
        </AdminRoute>
      ),
    },
  ];

  const clientRoutes = [
    {
      index: true,
      element: <ChatSection />,
    },
    {
      path: 'chat/:chatId',
      element: <ChatSection />,
    },
    {
      path: 'conversations',
      element: (
        // InsightsRoute, not AdminRoute: plain User holds conversations:read
        // and reaches this page, but is not in legacyAdmin.
        <InsightsRoute
          permission={[
            'conversations:read',
            'visitors:read',
            'queries:read',
            'regions:read',
          ]}
        >
          <Conversations />
        </InsightsRoute>
      ),
    },
    {
      path: 'leads',
      element: (
        <InsightsRoute permission={['leads:read', 'leads:analytics']}>
          <Leads />
        </InsightsRoute>
      ),
    },
    {
      path: 'theme',
      element: <Theme />,
    },
    {
      path: 'activeIntegrations',
      element: <SubscribedPages />,
    },
    {
      path: 'tickets',
      element: (
        <AdminRoute permission='hubspot:read'>
          <HubspotTickets />
        </AdminRoute>
      ),
    },
    {
      path: 'documentation',
      element: (
        <AdminRoute permission='documentation:read'>
          <Documentation />
        </AdminRoute>
      ),
    },
    {
      path: 'report',
      element: (
        <AdminRoute permission='metrics:read'>
          <Report />
        </AdminRoute>
      ),
    },
    {
      path: 'beacon',
      element: (
        <AdminRoute permission='beacon:read'>
          <Beacon />
        </AdminRoute>
      ),
    },
    {
      path: 'agentTraining',
      element: (
        <AdminRoute permission={[
            'bots:read',
            'files:read',
            'media:read',
            'leadSources:read',
            'prompt:read',
          ]}>
          <Training />
        </AdminRoute>
      ),
    },
    {
      path: 'automation-logs',
      element: (
        <AdminRoute permission='rules:read'>
          <RedisJobs />
        </AdminRoute>
      ),
    },
    {
      path: 'account',
      element: (
        <AdminRoute permission={[
            'settings:read',
            'clients:read',
            'whatsapp:read',
            'branches:read',
            'regions:read',
            'bots:read',
          ]}>
          <Account />
        </AdminRoute>
      ),
    },
    // Custom roles are hidden for now — see utils/featureFlags.ts.
    ...(CUSTOM_ROLES_ENABLED
      ? [
          {
            path: 'access-management',
            element: (
              <RequirePermission permission='roles:read'>
                <AccessManagement />
              </RequirePermission>
            ),
          },
        ]
      : []),
    {
      path: 'team',
      element: (
        <AdminRoute permission='team:read'>
          <Team />
        </AdminRoute>
      ),
    },
    {
      path: 'locations',
      // Locations is part of Configurations now, so this standalone route asks
      // for the same thing. Guarding it on branches:read alone let an
      // Overview-only role in: insights:read `requires` both keys.
      element: (
        <AdminRoute
          permission={[
            'settings:read',
            'clients:read',
            'whatsapp:read',
            'branches:read',
            'regions:read',
          ]}
        >
          <Branches />
        </AdminRoute>
      ),
    },
    {
      path: 'bulk-locations',
      element: (
        <AdminRoute permission='branches:manage'>
          <BulkBranches />
        </AdminRoute>
      ),
    },
    {
      path: 'invite',
      element: (
        <AdminRoute permission='team:invite'>
          <ExcludeRoute role={[ROLE.USERKEEPME, ROLE.KEEPME]}>
            <Invite />
          </ExcludeRoute>
        </AdminRoute>
      ),
    },
    {
      path: 'integrations',
      element: (
        <AdminRoute permission='mediaIntegrations:read'>
          <ExcludeRoute role={[ROLE.USERKEEPME, ROLE.KEEPME]}>
            <Integrations />
          </ExcludeRoute>
        </AdminRoute>
      ),
    },
    {
      path: 'integrations/:platform',
      element: (
        <AdminRoute permission='mediaIntegrations:read'>
          <Platform />
        </AdminRoute>
      ),
    },
    {
      path: 'billing',
      element: (
        <AdminRoute permission={['billing:read', 'clients:read']}>
          <Billing />
        </AdminRoute>
      ),
    },
    {
      path: 'automation',
      element: (
        <AdminRoute permission={[
            'rules:read',
            'automation:read',
            'whatsapp:read',
            'leads:read',
          ]}>
          <Rule />
        </AdminRoute>
      ),
    },
    {
      path: 'automation/ember/payment',
      element: (
        <AdminRoute permission={['memberPayment:read', 'overdueRule:read']}>
          <PaymentMembers />
        </AdminRoute>
      ),
    },
    {
      path: 'automation/ember/payment/status',
      element: (
        <AdminRoute permission={['memberPayment:read', 'overdueRule:read']}>
          <PaymentMemberStatus />
        </AdminRoute>
      ),
    },
    {
      path: 'group-automation',
      element: (
        <AdminRoute permission='rules:manage'>
          <BulkRule />
        </AdminRoute>
      ),
    },
    {
      path: 'campaign',
      element: (
        <AdminRoute permission={[
            'reengage:read',
            'regions:read',
            'clients:read',
          ]}>
          <ReEngage />
        </AdminRoute>
      ),
    },
    {
      path: 'templates',
      element: (
        <AdminRoute permission='templates:read'>
          <Templates />
        </AdminRoute>
      ),
    },
    {
      path: 'nova',
      element: (
        <InsightsRoute permission='insights:read'>
          <Nova />
        </InsightsRoute>
      ),
    },
    {
      path: 'improvement',
      element: (
        <AdminRoute permission='insights:analytics'>
          <Improvement />
        </AdminRoute>
      ),
    },
    {
      path: 'learnings',
      element: (
        <AdminRoute permission='insights:analytics'>
          <Learnings />
        </AdminRoute>
      ),
    },
    {
      path: 'benchmark',
      element: (
        <AdminRoute permission='insights:analytics'>
          <BenchmarkDashboard />
        </AdminRoute>
      ),
    },
    {
      path: 'performance',
      element: (
        <AdminRoute permission='insights:analytics'>
          <Performance />
        </AdminRoute>
      ),
    },
    {
      path: 'automation-performance',
      element: (
        <AdminRoute permission='insights:analytics'>
          <AutomationDashboard />
        </AdminRoute>
      ),
    },
    {
      path: 'template-performance',
      element: (
        <AdminRoute permission='insights:analytics'>
          <TemplateDashboard />
        </AdminRoute>
      ),
    },
    {
      path: 'scheduled-report',
      element: (
        <AdminRoute permission='insights:analytics'>
          <ScheduleReport />
        </AdminRoute>
      ),
    },
    {
      path: 'ember',
      element: (
        <AdminRoute permission='insights:read'>
          <Ember />
        </AdminRoute>
      ),
    },
    {
      path: 'atlas',
      element: (
        <AdminRoute permission='insights:read'>
          <Atlas />
        </AdminRoute>
      ),
    },
    {
      // The member records behind the Atlas overview cards. Same permission as
      // the overview, since it is the same data in list form.
      path: 'atlas/members',
      element: (
        <AdminRoute permission='insights:read'>
          <AtlasMemberRecords />
        </AdminRoute>
      ),
    },
    {
      path: 'clarion',
      element: (
        <AdminRoute permission='insights:read'>
          <Clarion />
        </AdminRoute>
      ),
    },
    {
      // The call records behind the Clarion overview cards. Same permission as
      // the overview, since it is the same data in list form.
      path: 'clarion/calls',
      element: (
        <AdminRoute permission='insights:read'>
          <ClarionCallRecords />
        </AdminRoute>
      ),
    },
    {
      path: 'aeo-query',
      element: (
        <AdminRoute permission='beacon:read'>
          <AeoQuery />
        </AdminRoute>
      ),
    },
    {
      path: 'beacon-live',
      element: (
        <AdminRoute permission='beacon:read'>
          <BeaconLive />
        </AdminRoute>
      ),
    },
    {
      path: 'query-feedback',
      element: (
        <AdminRoute permission='queryFeedback:read'>
          <QueryFeedbacks />
        </AdminRoute>
      ),
    },
    {
      path: 'google-analytics',
      element: (
        <AdminRoute permission='insights:read'>
          <AnalyticsDashboard />
        </AdminRoute>
      ),
    },
  ];

  const privateRoutes = [
    ...globalRoutes,
    {
      path: ':clientId',
      element: <ClientLayout />,
      children: clientRoutes,
    },
  ];

  const publicRoutes = [
    {
      path: '/demo',
      element: <Demo />,
    },
    {
      path: '/feedback',
      element: <Feedback />,
    },
    {
      path: '/voice',
      element: <VoiceDemo />,
    },
    {
      path: '/vai',
      element: <VoiceAgentPublic />,
    },
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/about',
      element: <About />,
    },
    {
      path: '/deletion',
      element: <FacebookDeletion />,
    },
    {
      path: '/forgot-password',
      element: <ForgotPassword />,
    },
    {
      path: '/password/:token',
      element: <Password />,
    },
    {
      path: '/twofa/',
      element: <TwoFa />,
    },
    {
      path: '/unauthorized',
      element: <Unauthorized />,
    },
    {
      path: '/login-success',
      element: <LoginSuccess />,
    },
    {
      path: '/mcp-authorize',
      element: <McpAuthorize />,
    },
    {
      path: '*',
      element: <Navigate to='/' replace />,
    },
  ];

  const routes = [
    {
      path: '/',
      element: (
        <PrivateRoute>
          <Layout />
        </PrivateRoute>
      ),
      // AN-1327: wrap every routed page in an ErrorBoundary so a failed
      // dynamic import (e.g. a stale code-split chunk after a deploy) or any
      // other render error shows a fallback / triggers a recovery reload
      // instead of unmounting the whole app to a white blank screen.
      children: privateRoutes.map((route) => ({
        ...route,
        element: <RouteErrorBoundary>{route.element}</RouteErrorBoundary>,
      })),
    },
    ...publicRoutes,
  ];

  return <Suspense fallback={<Loading />}>{useRoutes(routes)}</Suspense>;
}

export default Router;